import {downloadCSVFiles, getDateWithTimeString} from '@/helpers/shadcn/utils'
import {PromotionListColConfig} from '@/services/MockData/Promotions/PromotionListColConfig'
import {
  downloadPromotionFileUploadErrors,
  downloadPromotionList,
  downloadPromotionTemplate,
  downloadUploadedPromotionFile,
  getPromotionFileUploadErrors,
  getPromotionFileUploadHistory,
  getPromotionList,
} from '@/services/promotionListService'
import {DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {ROUTES} from '@/utils/constants/routesConstants'
import {getBlobUrlWithSasToken} from '@/utils/urlUtils'
import {useQuery} from '@tanstack/react-query'
import {useCallback, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useDataTable} from './useDataTable-hook'

export const usePromotionList = () => {
  const {
    setSortingObj,
    setSearchArry,
    pageIndex,
    currentPage,
    sortingObj,
    searchArr,
    onPageIndexChange,
    createSearchArray,
    resetAllBtnClicked,
  } = useDataTable()
  const [btnDisabled, setBtnDisabled] = useState(false)
  const formInitialData = {
    promotionTemplateName: '',
    storeSelector: '',
    actions: '',
    numberOfDays: '',
    numberOfPoints: '',
    schedule: '',
    startDate: '',
    endDate: '',
    mediaFile: [],
  }
  const [formData, setFormData] = useState({...formInitialData})
  const [isMultipleImagesModal, toggleMultipleImagesModal] = useState(false)
  const navigate = useNavigate()
  const [isSelectAll, setSelectAll] = useState(false)
  const [isModal, toggleModal] = useState(false)
  const [openUploadHistory, setOpenUploadHistory] = useState(false)
  const [rowSelected, setSelectedRows] = useState([])

  // Handler for "New Promotion" button click, navigates to the promotion creation page
  const onNewPromotionClick = useCallback(() => {
    navigate(ROUTES.SELECT_PROMOTION_TYPE)
  }, [navigate])

  const handleCopyClick = useCallback(
    (promotionId, promotionType) => {
      navigate(`/products/create-promotion/${promotionId}`, {
        state: {
          promotionId,
          isDuplicating: true,
          promotionType,
        },
      })
    },
    [navigate]
  )

  // Memoized query parameters string for the API request
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    // Sorting
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('sortOrder', sortingObj.direction)
    } else {
      // Apply default sorting if no specific sorting is set
      params.append('sortBy', 'activeStartDate')
      params.append('sortOrder', 'DESC')
    }

    // Pagination - always include page and pageSize
    params.append('page', pageIndex.toString())
    params.append('pageSize', DEFAULT_TABLE_PAGE_SIZE.toString())

    return params.toString()
  }, [sortingObj, pageIndex])

  // Fetches promotion list data using react-query
  const {data: promotionListResult, isLoading} = useQuery({
    queryKey: ['PROMOTION_LIST', queryParams, searchArr],
    queryFn: () => {
      return getPromotionList(searchArr, queryParams)
    },
  })

  // Fetches promotion upload history data using react-query
  const {data: promotionListUploadHistory} = useQuery({
    queryKey: ['PROMOTIONS_UPLOAD_HISTORY'],
    queryFn: () => getPromotionFileUploadHistory(),
  })

  // Handler for the download button click
  const onDownloadBtnClick = useCallback(
    async selectedIds => {
      if (btnDisabled) {
        return
      }
      setBtnDisabled(true)

      const payload = {}

      // Add 'ids' to payload if selectedIds is not empty
      if (selectedIds && selectedIds.length > 0) {
        payload.ids = selectedIds
      }

      // Add 'filters' to payload if searchArr is not empty
      if (searchArr && searchArr.length > 0) {
        payload.filters = searchArr
      }

      // Determine the actual body to send. If payload has no keys, send null. Otherwise, send payload.
      const requestBody = Object.keys(payload).length > 0 ? payload : null

      try {
        const response = await downloadPromotionList(requestBody, queryParams)
        downloadCSVFiles(response, `PromotionList_${getDateWithTimeString()}`)
        setBtnDisabled(false)
      } catch (error) {
        console.error('Download API call failed:', error)
        setBtnDisabled(false)
      }
    },
    [btnDisabled, searchArr, queryParams]
  )

  const promotionListData = useMemo(() => {
    return promotionListResult || {}
  }, [promotionListResult])
  // Function to download the promotion template file
  const downloadPromoTemplate = async () => {
    const response = await downloadPromotionTemplate()

    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = 'PromotionTemplate.xlsx'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    URL.revokeObjectURL(url)
  }

  // Funciton to download promotion file upload errors
  const getErrorTemplateFilename = () => {
    const date = new Date()
    const timestamp = date.toISOString().replace(/[-:]/g, '').replace('T', '_').split('.')[0]
    return `Upload_Error_Template_${timestamp}`
  }

  const downloadUploadError = async fileUUID => {
    const response = await downloadPromotionFileUploadErrors(fileUUID)

    await downloadCSVFiles(response, getErrorTemplateFilename())
  }

  // Funtion to download uploaded promotion file
  const downloadPromotionFile = async fileUUID => {
    try {
      const response = await downloadUploadedPromotionFile(fileUUID)
      const data = response?.data
      const downloadUrl = getBlobUrlWithSasToken(data) || `${data.blobUrl}${data.fileName}?${data.sasToken}`

      // Fetch the file
      const blobURL = await fetch(downloadUrl)

      if (!blobURL.ok) {
        const errorText = await blobURL.text()
        console.error('Download failed:', errorText)
        throw new Error(`Download failed: ${blobURL.status}`)
      }

      const blob = await blobURL.blob()

      // Create  and click download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = data.fileName

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(url)

      return downloadUrl
    } catch (error) {
      console.error('Error downloading promotion file:', error)
      throw error
    }
  }

  const handleResetAll = () => {
    setSortingObj({})
    createSearchArray([])
    setSearchArry({})
    setSelectedRows([])
  }

  const handleSorting = ({column, direction}) => {
    setSortingObj({column, direction})
  }

  const handlePageIndexChange = pageIndex => {
    onPageIndexChange(pageIndex)
  }

  const tableActions = useMemo(
    () => [
      // Primary action – create a new promotion
      {
        id: 'new-promotion',
        label: 'New Promotion',
        icon: 'add_box',
        onClick: () => onNewPromotionClick(),
        disabled: false,
      },
      // Divider between "New" and other actions – use a simple object flag
      {divider: true},
      // Existing download action

      {
        id: 'upload',
        label: 'Upload Bulk Promotions',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelecteds
          toggleModal(!isModal)
        },
      },
      {
        id: 'upload_images',
        label: 'Upload Images',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelecteds
          toggleMultipleImagesModal(!isMultipleImagesModal)
        },
      },
      {
        id: 'upload_history',
        label: 'View Upload History',
        onClick: () => setOpenUploadHistory(true),
      },
      {
        id: 'download',
        label: 'Download',
        onClick: () => {
          // Use selectedRows from CustomDataTable if available, otherwise fall back to rowSelecteds
          const dataToDownload = isSelectAll ? [{}] : rowSelected
          const productIds = dataToDownload.map(promo => promo.id) 

          onDownloadBtnClick(productIds)
        },
        disabled: ({selectedRows}) => !selectedRows?.length, // Dynamic disable based on selection
      },
    ],
    [isSelectAll, onDownloadBtnClick, onNewPromotionClick, rowSelected, isModal, isMultipleImagesModal]
  )

  return {
    promotionListData,
    promotionListColConfigData: PromotionListColConfig,
    currentPage,
    setSortingObj,
    setSearchArry,
    onDownloadBtnClick,
    onPageIndexChange,
    isLoading,
    createSearchArray,
    resetAllBtnClicked,
    formData,
    setFormData,
    downloadPromoTemplate,
    downloadPromotionFile,
    getPromotionFileUploadErrors,
    downloadUploadError,
    uploadHistoryData: promotionListUploadHistory || [],
    toggleMultipleImagesModal,
    isMultipleImagesModal,
    onNewPromotionClick,
    handleCopyClick,
    handleResetAll,
    handleSorting,
    handlePageIndexChange,
    isSelectAll,
    setSelectAll,
    isModal,
    toggleModal,
    openUploadHistory,
    setOpenUploadHistory,
    rowSelected,
    setSelectedRows,
    tableActions,
  }
}
